import * as THREE from 'three'
import { types, getParent } from 'mobx-state-tree'
import { EngravingProperties } from 'src/api/models/Elements/Element/ElementProperties'
import { Font } from 'src/api/misc/Font'
import { ExtendedFont } from 'src/api/misc/Font/types'
// import { generateNodeType, generateType } from 'src/api/models/utils/generateNodeType'
import { generateElementType } from 'src/api/models/Elements/Element/generateElementType'
import { textEngravingElement } from 'src/constants/typenames'
import {
  StringValue,
  UnitNumberValue,
  generateEnumValue
} from 'src/api/models/values'
import { buildStrokedShapes } from 'src/components/viewer/elements/shapes'
import {
  ITextEngravingProperties,
  ITextEngragvingName,
  ITextEngravingElement,
  IBuildGeometry
} from './types'

const getWidth = ({
  boundingBox,
  scale,
  rowLength
}: Pick<ExtendedFont['data'], 'boundingBox'> & {
  scale: number
  rowLength: number
}) => (boundingBox.xMax - boundingBox.xMin) * scale * rowLength

const getHeight = ({
  boundingBox,
  underlineThickness,
  scale,
  amountOfRows
}: Pick<ExtendedFont['data'], 'boundingBox' | 'underlineThickness'> & {
  scale: number
  amountOfRows: number
}) =>
  (boundingBox.yMax - boundingBox.yMin + underlineThickness) *
  scale *
  amountOfRows

/** */

const TextEngravingProperties: ITextEngravingProperties = EngravingProperties.props(
  {
    text: types.optional(StringValue, { value: 'new element' }),
    font: types.optional(types.reference(Font), 'helvetiker'),
    fontSize: types.optional(UnitNumberValue, { value: 5 }),
    fontWeight: types.maybe(types.number),
    textAlign: types.optional(
      generateEnumValue(['center', 'left', 'right']).views(() => ({
        get type() {
          return 'select'
        }
      })),
      { value: 'center' }
    )
  }
)
  .views((self) => {
    const font = self.font.instance()

    return {
      get geometry() {
        if (font instanceof Promise) {
          return font.then((font) => buildGeometry(font, self))
        }
        return buildGeometry(font, self)
      }
    }
  })
  .views((self) => ({
    get width() {
      const size = new THREE.Vector3()
      if (self.geometry instanceof Promise) {
        self.geometry.then((geometry) => {
          if (!geometry.boundingBox) geometry.computeBoundingBox()
          geometry.boundingBox?.getSize(size)
        })
      } else {
        if (!self.geometry.boundingBox) self.geometry.computeBoundingBox()
        self.geometry.boundingBox?.getSize(size)
      }
      return { value: size.z }
    },
    get height() {
      const size = new THREE.Vector3()
      if (self.geometry instanceof Promise) {
        self.geometry.then((geometry) => {
          if (!geometry.boundingBox) geometry.computeBoundingBox()
          geometry.boundingBox?.getSize(size)
        })
      } else {
        if (!self.geometry.boundingBox) self.geometry.computeBoundingBox()
        self.geometry.boundingBox?.getSize(size)
      }
      return { value: size.x }
    }
  }))

const buildGeometry: IBuildGeometry = (font, self) => {
  // console.log('ONGEOMEETRY', self.text.value)
  const shapes = font.generateShapes(self.text.value, self.fontSize.value)
  let geometry
  if (self.style.value === 'fill') {
    geometry = new THREE.ShapeBufferGeometry(shapes)
  } else {
    geometry = buildStrokedShapes({ shapes, config: { strokeWidth: 0.2 } })
  }
  geometry
    .center()
    .rotateX(Math.PI / -2)
    .rotateY(Math.PI / 2)
  geometry.computeBoundingBox()
  return geometry
}

const TextEngragvingName: ITextEngragvingName = StringValue.actions((self) => ({
  afterAttach() {
    if (self.value === '') {
      const element = getParent<ITextEngravingElement>(self)
      self.value = `"${element.properties.text.value}" Text Engraving Element`
    }
  }
}))

export const TextEngravingElement: ITextEngravingElement = generateElementType(
  textEngravingElement
).props({
  name: types.optional(TextEngragvingName, { value: '' }),
  properties: types.optional(TextEngravingProperties, {})
}) as ITextEngravingElement
